<template>
  <div>
    <data-table
      ref="dataTableRef"
      :config="dataTableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

// Get i18n composable
const { t } = useI18n();
const { initialized } = useFiltro();

// Reactive data
const dataTableRef = ref<DataTableInstance | null>(null);

// Computed properties
const endpoint = computed(() => '/index.php/relatorios/relatorio_clientes/relatorioClientes');

const tableColumns = computed(() => {
  return [
    {
      data: 'cliente_id',
      title: t('Cliente ID'),
      renderType: 'custom_clickable_id',
      width: 100,
      sortable: true,
      clickCallback: handleClienteClick
    },
    {
      data: 'compromisso_id',
      title: t('Compromisso ID'),
      renderType: 'custom_clickable_id',
      width: 130,
      sortable: true,
      clickCallback: handleCompromissoClick
    },
    {
      data: 'status',
      title: t('Status'),
      width: 160,
      sortable: true
    },
    {
      data: 'total',
      title: t('Valor Total'),
      width: 130,
      sortable: true,
      renderType: 'currency',
      footerType: 'sum'
    },
    {
      data: 'cpf',
      title: t('Documento'),
      width: 140,
      sortable: true
    },
    {
      data: 'nome',
      title: t('Nome Completo'),
      width: 250,
      sortable: true
    },
    {
      data: 'endereco',
      title: t('Endereço Completo'),
      width: 300,
      sortable: false
    },
    {
      data: 'localidade',
      title: t('Localidade'),
      width: 180,
      sortable: true
    },
    {
      data: 'celular',
      title: t('Celular'),
      width: 150,
      sortable: true
    },
    {
      data: 'email',
      title: t('E-mail'),
      width: 220,
      sortable: true
    },
    {
      data: 'data_hora_inicio',
      title: t('Data do Compromisso'),
      width: 180,
      sortable: true,
      renderType: 'datetime'
    },
    {
      data: 'data_cadastro',
      title: t('Data Cadastro'),
      width: 150,
      sortable: true,
      renderType: 'date'
    },
    {
      data: 'forma_pagamento',
      title: t('Forma de Pagamento'),
      width: 180,
      sortable: true
    },
    {
      data: 'franquia',
      title: t('Franquia'),
      width: 150,
      sortable: true,
      footerType: 'unique'
    }
  ];
});

const dataTableConfig = computed(() => {
  return {
    tableId: 'tabelaRelatorioClientes',
    filename: `relatorio_clientes_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 50,
    pagination: true,
    footerLabel: t('Totais'),
    rowSelection: 'single',
    resizableColumns: true,
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const filterConfig = computed(() => {
  return {
    searchName: t('Procurar'),
    choosePlaceholder: t('Escolha'),
    refreshName: t('Atualizar'),
    printName: t('Imprimir'),
    debug: false,
    columns: {
      'cliente_id': 'text',
      'compromisso_id': 'text',
      'status': 'select',
      'total': 'text',
      'cpf': 'text',
      'nome': 'text',
      'endereco': 'text',
      'localidade': 'select',
      'celular': 'text',
      'email': 'text',
      'data_hora_inicio': 'date',
      'data_cadastro': 'date',
      'forma_pagamento': 'select',
      'franquia': 'select'
    }
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV'),
    loading: false
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

// Methods
const handleClienteClick = (clienteId: Id | undefined): void => {
  log('RelatorioClientesTable', 'handleClienteClick', 'Opening cliente', clienteId);
  eventBus.emit('modal.cliente.open', { id: clienteId });
};

const handleCompromissoClick = (compromissoId: Id | undefined): void => {
  log('RelatorioClientesTable', 'handleCompromissoClick', 'Opening compromisso', compromissoId);
  eventBus.emit('modal.gestao-compromisso.open', { id: compromissoId });
};

const loadData = () => {
  log('RelatorioClientesTable', 'loadData', '========== LOAD DATA START ==========');
  const stack = new Error().stack;
  log('RelatorioClientesTable', 'loadData', 'Call stack:', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  log('RelatorioClientesTable', 'loadData', 'dataTableRef exists:', !!dataTableRef.value);
  log('RelatorioClientesTable', 'loadData', 'Endpoint:', endpoint.value);

  if (dataTableRef.value) {
    log('RelatorioClientesTable', 'loadData', 'Calling dataTableRef.loadData()...');
    dataTableRef.value.loadData();
    log('RelatorioClientesTable', 'loadData', 'dataTableRef.loadData() called');
  } else {
    log('RelatorioClientesTable', 'loadData', 'ERROR: dataTableRef is null!');
  }
  log('RelatorioClientesTable', 'loadData', '========== LOAD DATA END ===========');
};

const handleDataLoaded = (data: unknown[]): void => {
  log('RelatorioClientesTable', 'handleDataLoaded', '========== DATA LOADED ==========');
  log('RelatorioClientesTable', 'handleDataLoaded', 'Data type:', typeof data);
  log('RelatorioClientesTable', 'handleDataLoaded', 'Data length:', data?.length || 0);
  log('RelatorioClientesTable', 'handleDataLoaded', 'Data:', data);
  eventBus.emit('globalMessage.success', t('Carregados') + ` ${data?.length || 0} ` + t('registros de clientes'));
  log('RelatorioClientesTable', 'handleDataLoaded', '========== DATA LOADED END ==========');
};

const handleDataError = (error: { message?: string; name?: string; stack?: string; constructor?: { name: string } } | Error): void => {
  log('RelatorioClientesTable', 'handleDataError', '========== DATA ERROR ==========');
  log('RelatorioClientesTable', 'handleDataError', 'Error type:', error instanceof Error ? error.constructor.name : (error as { constructor?: { name: string } })?.constructor?.name);
  log('RelatorioClientesTable', 'handleDataError', 'Error name:', error instanceof Error ? error.name : (error as { name?: string })?.name);
  log('RelatorioClientesTable', 'handleDataError', 'Error message:', error?.message);
  log('RelatorioClientesTable', 'handleDataError', 'Full error:', error);
  log('RelatorioClientesTable', 'handleDataError', 'Error stack:', error instanceof Error ? error.stack : (error as { stack?: string })?.stack);
  const message = error?.message || t('Erro desconhecido');
  eventBus.emit('globalMessage.error', t('Erro ao carregar relatório de clientes') + ': ' + message);
  log('RelatorioClientesTable', 'handleDataError', '========== DATA ERROR END ==========');
};

// Event handler for filter updates (named for proper cleanup)
const onFilterUpdated = () => {
  log('RelatorioClientesTable', 'event:filter.updated', 'Received, reloading data');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('RelatorioClientesTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  // Don't load data on mount - let DataTable handle initial load via autoLoad
  // Only reload when filter is explicitly updated
  log('RelatorioClientesTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  // Set up event listeners
  eventBus.on('filter.updated', onFilterUpdated);
});

onBeforeUnmount(() => {
  log('RelatorioClientesTable', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up event listeners
  eventBus.off('filter.updated', onFilterUpdated);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
</style>
